import { useState, useRef, useEffect } from 'react'

import { TbCircleDashedLetterG } from 'react-icons/tb'

import { useApi } from '@/hooks'

import { useMapContext } from './MapContext'

interface GeofenceCategory {
  id: number
  name: string
}

export const MapGeofenceSelection = () => {
  const context = useMapContext()
  const { companyId } = context.filters
  const { api } = useApi()
  const [showGeofenceDropdown, setShowGeofenceDropdown] = useState(false)
  const [availableCategories, setAvailableCategories] = useState<GeofenceCategory[]>([])
  const dropdownRef = useRef<HTMLDivElement>(null)
  const lastCompanyIdRef = useRef<number | undefined>(undefined)
  // Fetch available z-orders and categories once per company
  useEffect(() => {
    if (!companyId) {
      setAvailableCategories([])
      context.setMapState((prev) => ({ ...prev, zOrders: [], geofenceCategoryIds: [] }))
      lastCompanyIdRef.current = undefined
      return
    }
    const companyChanged = lastCompanyIdRef.current !== undefined && lastCompanyIdRef.current !== companyId
    lastCompanyIdRef.current = companyId

    const fetchCategories = async () => {
      try {
        const path = `/fresh/geofence-categories?companyIds=${companyId}&skip=0&take=1000`
        const response = await api.get<GeofenceCategory[]>({ path, origin: 'MapToggles' })
        const { data: cats } = response
        // For now, don't try to match up category names across companies
        if (companyChanged) {
          context.setMapState((prev) => ({ ...prev, geofenceCategoryIds: [] }))
        }
        setAvailableCategories(cats && cats.length > 0 ? cats : [])
      } catch (error) {
        console.error('Error fetching categories:', error)
        setAvailableCategories([])
      }
    }

    fetchCategories()
  }, [companyId, api, context.setMapState])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowGeofenceDropdown(false)
      }
    }

    if (showGeofenceDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showGeofenceDropdown])

  const toggleCategory = (categoryId: number) => {
    const current = context.mapState.geofenceCategoryIds || []
    const geofenceCategoryIds = current.includes(categoryId)
      ? current.filter((z) => z !== categoryId)
      : [...current, categoryId].sort()
    context.setMapState({ ...context.mapState, geofenceCategoryIds })
  }

  const selectAllCategories = () => {
    context.setMapState({ ...context.mapState, geofenceCategoryIds: [...availableCategories.map((c) => c.id)] })
  }

  const clearAllCategories = () => {
    context.setMapState({ ...context.mapState, geofenceCategoryIds: [] })
  }

  return (
    <div className="relative shrink-0" ref={dropdownRef}>
      <button
        data-tip="Geofence filter"
        onClick={() => setShowGeofenceDropdown(!showGeofenceDropdown)}
        className={`tooltip tooltip-right tooltip-primary btn btn-sm btn-square shadow-lg border border-base-300 ${
          context.mapState.zOrders.length > 0 || context.mapState.geofenceCategoryIds.length > 0
            ? 'btn-primary'
            : 'bg-base-100 hover:bg-base-200'
        }`}
      >
        <TbCircleDashedLetterG className="w-4 h-4" style={{ strokeDasharray: '4,2' }} />
      </button>
      {showGeofenceDropdown && (
        <div className="absolute top-0 left-full bg-base-100 rounded-r shadow-lg border border-base-300 p-2 min-w-[260px] max-h-[400px] overflow-y-auto">
          {/* Categories Section - only show if categories exist */}
          {availableCategories.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2 px-2">
                <div className="text-xs font-semibold">Categories</div>
                <div className="flex gap-1">
                  <button
                    onClick={selectAllCategories}
                    className="text-xs btn btn-ghost btn-xs px-1 py-0 h-auto min-h-0"
                    title="Select All"
                  >
                    All
                  </button>
                  <button
                    onClick={clearAllCategories}
                    className="text-xs btn btn-ghost btn-xs px-1 py-0 h-auto min-h-0"
                    title="Clear All"
                  >
                    Clear
                  </button>
                </div>
              </div>
              {availableCategories.map((category) => (
                <label
                  key={category.id}
                  className="flex items-center gap-2 px-2 py-1 hover:bg-base-200 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={(context.mapState.geofenceCategoryIds || []).includes(category.id)}
                    onChange={() => toggleCategory(category.id)}
                  />
                  <span className="text-xs">{category.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
